import cx from 'classnames';
import useOnclickOutside from 'react-cool-onclickoutside';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';

import { useGetBetaTesterAgencies, useGetLoggedAgent } from '../../resources/agent/hooks';
import { useTrackEventAmplitude } from '../../shared/analytics';
import { setEntryPoint } from '../../shared/analytics/sessionStorage-utilities';
import { getPROHostByLocale } from '../../shared/intl/operations';
import { AvailableLocale } from '../../shared/intl/types';
import { Close } from '../Icons/Close';
import { Logo } from '../SVG/Logo';

import s from './Menu.css';

type Props = {
    locale: AvailableLocale;
    opened: boolean;
    onClose: () => void;
    username: string;
    status: 'exiting' | 'exited' | 'entered' | 'entering' | 'unmounted';
};

const links = [
    {
        pathname: '/process/sales',
        Label: 'Pipeline',
    },
    {
        pathname: '/todo/today',
        Label: 'To Do',
    },
    {
        pathname: '/homepage',
        Label: 'Inbox',
    },
    {
        pathname: '/templates/list',
        Label: <FormattedMessage id="templates.menu" />,
        'data-testid': 'template-list-menu-link',
    },
];

export const Menu = ({ locale, opened, onClose, username, status }: Props): JSX.Element => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const { pathname: globalPathname } = useLocation();

    const PROHost = getPROHostByLocale(locale);

    // [AT1_AUTO_EXPERIMENT] Check if agent's agency is eligible for AT1 auto settings
    const agent = useGetLoggedAgent();
    const { data: betaTesterAgencies } = useGetBetaTesterAgencies();
    const showAT1AutoSettings =
        agent !== null &&
        betaTesterAgencies?.some((beta) => beta.agencyId === Number(agent.agencyId));

    const ref = useOnclickOutside(() => {
        if (opened) {
            onClose();
        }
    });

    const inbentaLocaleToRenderLink =
        locale === 'fr' ? '/knowledge-base?locale=fr' : '/knowledge-base?locale=en';

    return (
        <div
            className={cx(s.menuContainer, {
                [s.hiddenMenu]: status === 'entering',
                [s.showMenu]: status === 'entered',
            })}
            ref={ref}
            data-testid="menu"
        >
            <div className={s.header}>
                <div className={s.headerContent}>
                    <Logo />
                    <div className={s.usernameBadge}>
                        <p className="ink">👋 {username}</p>
                    </div>
                </div>
                <Close className={s.close} onClick={onClose} />
            </div>
            <div className={s.links}>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>Request Manager</h3>

                    {links.map(({ pathname, Label, ...rest }) => (
                        <NavLink
                            activeClassName={s.active}
                            key={pathname}
                            onClick={() => {
                                if (Label === 'Inbox' && !globalPathname.includes('homepage')) {
                                    setEntryPoint('inbox_button');
                                }
                                onClose();
                            }}
                            className={s.link}
                            to={{ pathname }}
                            {...rest}
                        >
                            {Label}
                        </NavLink>
                    ))}
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({ id: 'menu.offer_manager.title' })}
                    </h3>
                    <a
                        onClick={onClose}
                        href="/offer-manager"
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.offer_manager.itineraries',
                        })}
                    </a>
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({
                            id: 'menu.tipi.title',
                        })}
                    </h3>
                    <a
                        onClick={onClose}
                        href="/trip-planner/library"
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.tipi.library',
                        })}
                    </a>
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({
                            id: 'menu.cash_manager.title',
                        })}
                    </h3>
                    <a
                        onClick={onClose}
                        href={`https://${PROHost}/transaction/#/payments`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.accounting',
                        })}
                    </a>
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({
                            id: 'menu.insights.title',
                        })}
                    </h3>
                    <a
                        onClick={onClose}
                        href={`/agency-insights`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.insights.statistics',
                        })}
                    </a>
                    <a
                        onClick={onClose}
                        href={`/agency-insights/customer-satisfaction`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.insights.customer_satisfaction',
                        })}
                    </a>
                    <a
                        onClick={onClose}
                        href={`/agency-insights/better-trip-index`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.insights.better_trip_index',
                        })}
                    </a>
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({
                            id: 'menu.settings.title',
                        })}
                    </h3>
                    <a
                        onClick={onClose}
                        href={`/agency-manager/active-accounts`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.settings.agency_accounts',
                        })}
                    </a>
                    <a
                        onClick={onClose}
                        href={`/agency-manager/capacity`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.settings.agency_capacity',
                        })}
                    </a>
                    <a
                        onClick={onClose}
                        href={`/agency-manager/contacts/main`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.settings.main_contacts',
                        })}
                    </a>
                    <a
                        onClick={onClose}
                        href={`/agency-legal-information`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.settings.legal_informations',
                        })}
                    </a>
                    {/* [AT1_AUTO_EXPERIMENT] Conditional link for AT1 auto settings (beta testers only) */}
                    {showAT1AutoSettings && (
                        <a
                            onClick={onClose}
                            href={`/agency-manager/automated-message-settings`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={s.link}
                        >
                            {formatMessage({
                                id: 'experiment.better_business.at1.menu.at1_settings',
                            })}
                        </a>
                    )}
                </div>
                <div className={s.linkContainer}>
                    <h3 className={cx(s.title, 'ink-light')}>
                        {formatMessage({
                            id: 'menu.tools.title',
                        })}
                    </h3>
                    <a
                        onClick={onClose}
                        href={'https://travelsuite.evaneos.com/agency-manager/community'}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'menu.tools.partner_connect',
                        })}
                    </a>
                    <a
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.faqClicked({
                                    ...defaultProperties,
                                    click_origin: 'ets menu',
                                });
                            });
                            onClose();
                        }}
                        href={inbentaLocaleToRenderLink}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={s.link}
                    >
                        {formatMessage({
                            id: 'navbar.help_center',
                        })}
                    </a>
                </div>
            </div>
        </div>
    );
};
